import { AvailableCompanions } from "@hooks/use-companion";
import Twemoji from "@src/Twemoji";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useEffect, useRef, useState } from "react";

const CompanionAutocompleteInput = ({
  value,
  onChange,
  placeholder,
  className = "",
  onKeyDown,
  ref,
  ...props
}) => {
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] =
    useState(-1);
  const [caretPosition, setCaretPosition] = useState(0);
  const suggestionsRef = useRef(null);
  const inputRef = useRef(null);
  const setInputRef = (node) => {
    inputRef.current = node;
    if (typeof ref === "function") {
      ref(node);
    } else if (ref) {
      ref.current = node;
    }
  };

  const { writingCompanion, researchCompanion } = useFlags();

  const suggestions = [
    AvailableCompanions.SUMMI,
    writingCompanion ? AvailableCompanions.QUILL : null,
    researchCompanion ? AvailableCompanions.SAGE : null,
  ].filter(Boolean);

  const companionSuggestions = suggestions.map(
    ({ name, displayName, description, emoji }) => ({
      name,
      displayName,
      description,
      icon: emoji,
    }),
  );

  const normalizedValue = value ?? "";
  const searchPosition = Math.min(
    caretPosition ?? normalizedValue.length,
    normalizedValue.length,
  );
  const fromIndex = searchPosition - 1;
  const atPosition = normalizedValue.lastIndexOf("@", fromIndex);
  let filteredSuggestions = [];
  if (atPosition !== -1) {
    const segmentEnd = Math.max(searchPosition, atPosition + 1);
    const afterAt = normalizedValue.slice(atPosition + 1, segmentEnd);
    const hasSpaceAfterAt = afterAt.includes(" ");

    const boundaryChar = normalizedValue[searchPosition];
    const boundaryTerminatesQuery =
      boundaryChar && /[\s\p{P}]/u.test(boundaryChar);

    if (!hasSpaceAfterAt && !boundaryTerminatesQuery) {
      const query = afterAt.toLowerCase();
      filteredSuggestions = companionSuggestions.filter(
        (suggestion) =>
          suggestion.name.toLowerCase().startsWith(query),
      );
    }
  }
  const activeSuggestionIndex =
    filteredSuggestions.length === 0
      ? -1
      : Math.min(
          selectedSuggestionIndex,
          filteredSuggestions.length - 1,
        );

  const selectSuggestion = (suggestion) => {
    if (atPosition === -1) {
      return;
    }

    const currentValue = value ?? "";
    const beforeAt = currentValue.slice(0, atPosition);
    const caret =
      caretPosition != null
        ? Math.max(caretPosition, atPosition + 1)
        : atPosition + 1;
    const queryEnd = Math.min(caret, currentValue.length);
    const afterQuery = currentValue.slice(queryEnd);
    const needsSpace =
      afterQuery.length === 0
        ? " "
        : /^[\p{P}\s]/u.test(afterQuery[0])
          ? ""
          : " ";

    const newValue = `${beforeAt}@${suggestion.name}${needsSpace}${afterQuery}`;
    const cursorPosition =
      beforeAt.length +
      1 +
      suggestion.name.length +
      needsSpace.length;

    setCaretPosition(cursorPosition);
    onChange(newValue);

    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.setSelectionRange(
          cursorPosition,
          cursorPosition,
        );
        inputRef.current.focus();
      }
    }, 0);

    setSelectedSuggestionIndex(-1);
  };

  const handleKeyDown = (e) => {
    const currentSuggestions = filteredSuggestions;
    const hasSuggestions = currentSuggestions.length > 0;
    if (hasSuggestions) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedSuggestionIndex((prev) =>
          prev < currentSuggestions.length - 1 ? prev + 1 : 0,
        );
        return;
      }

      if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedSuggestionIndex((prev) =>
          prev > 0 ? prev - 1 : currentSuggestions.length - 1,
        );
        return;
      }

      if (e.key === "Enter") {
        e.preventDefault();
        if (currentSuggestions.length === 1) {
          selectSuggestion(currentSuggestions[0]);
        } else if (activeSuggestionIndex >= 0) {
          selectSuggestion(currentSuggestions[activeSuggestionIndex]);
        }
        return;
      }

      if (e.key === "Escape") {
        setSelectedSuggestionIndex(-1);
        return;
      }
    }

    if (onKeyDown) {
      onKeyDown(e);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target)
      ) {
        setSelectedSuggestionIndex(-1);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Handle input changes
  const handleChange = (e) => {
    const newValue = e.target.value;
    const newCaretPosition =
      e.target.selectionStart ?? e.target.value?.length ?? 0;
    setCaretPosition(newCaretPosition);
    setSelectedSuggestionIndex(-1);
    onChange(newValue);
  };

  // Auto-resize textarea up to max-height (controlled by className)
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.style.height = "auto";
      inputRef.current.style.height = `${inputRef.current.scrollHeight}px`;
    }
  }, [value]);

  return (
    <div className="relative w-full">
      <textarea
        ref={setInputRef}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className={className}
        data-testid="companion-input"
        rows={2}
        {...props}
      />

      {filteredSuggestions.length > 0 && (
        <div
          ref={suggestionsRef}
          className="absolute bottom-full left-0 right-0 mb-2 bg-white dark:bg-indigo-800 border border-gray-200 dark:border-indigo-700 rounded-lg shadow-lg z-50 max-h-48 overflow-y-auto"
        >
          {filteredSuggestions.map((suggestion, index) => (
            <div
              key={suggestion.name}
              className={`px-4 py-3 cursor-pointer border-b border-gray-100 dark:border-indigo-700 last:border-b-0 ${
                index === activeSuggestionIndex
                  ? "bg-indigo-50 dark:bg-indigo-900"
                  : "hover:bg-gray-50 dark:hover:bg-indigo-700"
              }`}
              onClick={() => selectSuggestion(suggestion)}
            >
              <div className="flex items-center space-x-3">
                <Twemoji emoji={suggestion.icon} />
                <div className="flex-1">
                  <div className="flex items-center space-x-2">
                    <span className="font-medium text-gray-900 dark:text-gray-100">
                      @{suggestion.name}
                    </span>
                  </div>
                  <div className="text-xs text-gray-600 dark:text-indigo-300">
                    {suggestion.description}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CompanionAutocompleteInput;
